import React, { useState, useEffect } from 'react';
import { useRouteContext } from '../../context/RouteContext';
import ClaimsWizard from '../../components/claims/ClaimsWizard';
import OrderLookup from '../../components/shared/OrderLookup';
import OrderSummary from '../../components/shared/OrderSummary';
import { useTheme } from '../../context/PortalThemeContext';
import { useMockStep } from '../../hooks/useMockStep';
import { ClaimStates } from '../../components/claims/ClaimStates';
import { fetchWithSessionToken } from '../../components/shared/fetchWithSessionToken';
import { fetchDynamicOrder } from '../../utils/orderLookupApi';
import { CHAT_API_BASE } from '../../config/api';
import ReturnPolicyLink from '../../components/returns/ReturnPolicyLink';
import mockOrderData from '../../mock/mockOrderData.json';

export default function ClaimsPage() {
  const {
    customer,
    setCustomer,
    orderDetails,
    setOrderDetails,
    trackingInfo,
    setTrackingInfo,
    shopifyProducts,
    setShopifyProducts,
  } = useRouteContext();

  const [step, setStep] = useState(ClaimStates.ORDER_SUMMARY);
  const [claimDetails, setClaimDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const mockStep = useMockStep();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlOrderNumber = urlParams.get('orderNumber');
    const urlEmail = urlParams.get('email');
    const autoSearch = urlParams.get('autoSearch') === 'true';

    if (urlOrderNumber && urlEmail && autoSearch) {
      setOrderDetails(null);
      setCustomer(null);
      setTrackingInfo(null);
      setShopifyProducts(null);
      setStep(ClaimStates.ORDER_LOOKUP);
      return;
    }

    const local = localStorage.getItem('brella_order');
    if (local) {
      let parsed = null;
      try {
        parsed = JSON.parse(local);
      } catch (err) {
        // A corrupt value would otherwise throw and white-screen the page.
        // Drop it and fall through to the order-lookup flow.
        console.error('Corrupt brella_order in localStorage, clearing:', err);
        localStorage.removeItem('brella_order');
      }

      if (parsed) {
        setOrderDetails(parsed);
        if (parsed.customer) setCustomer(parsed.customer);
        if (parsed.trackingInfo) setTrackingInfo(parsed.trackingInfo);
        if (parsed.shopifyProducts) setShopifyProducts(parsed.shopifyProducts);
      }
    }
  }, []);

  // Fetch claim details when order has a claimId (only when not in mock mode)
  useEffect(() => {
    const fetchClaimDetails = async () => {
      // Skip API call if in mock mode
      if (mockStep) return;
      
      if (!orderDetails?.claimId || !orderDetails?.shopId) return;
      
      try {
        const response = await fetchWithSessionToken(
          `${CHAT_API_BASE}/companies/${orderDetails.shopId}/claims/${orderDetails.claimId}`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          }
        );
        
        if (response.ok) {
          const claimData = await response.json();
          setClaimDetails(claimData);
        }
      } catch (error) {
        console.error('Error fetching claim details:', error);
      }
    };

    fetchClaimDetails();
  }, [orderDetails?.claimId, orderDetails?.shopId, mockStep]);

  const handleCancelClaim = async (claimId) => {
    // Skip API call if in mock mode
    if (mockStep) {
      // console.log('Mock mode: Skipping claim cancellation API call');
      return;
    }
    
    if (!claimId || !orderDetails?.shopId) return;
    
    setIsLoading(true);
    try {
      const response = await fetchWithSessionToken(
        `${CHAT_API_BASE}/companies/${orderDetails.shopId}/claims/${claimId}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
        }
      );
      
      if (response.status === 204) {
        // Clear claimDetails if it was for the cancelled claim
        if (claimDetails?.id === claimId) {
          setClaimDetails(null);
        }
        
        // Fetch the dynamic order again to get updated claim information
        if (orderDetails.confirmation_number || orderDetails.order_number) {
          try {
            const refreshResponse = await fetchDynamicOrder(
              {
                companyId: orderDetails.shopId,
                searchParam: orderDetails.confirmation_number || orderDetails.order_number,
                additionalParam:
                  orderDetails.customer?.email ||
                  orderDetails?.shipping_address?.zip ||
                  orderDetails?.shipping_address?.phone,
              },
              { withSession: true }
            );
            
            if (refreshResponse.ok) {
              const refreshData = await refreshResponse.json();
              const refreshedOrder = { 
                ...orderDetails,
                orderClaims: refreshData.orderClaims || [],
                lineItemToClaimId: refreshData.lineItemToClaimId || {}
              };
              
              setOrderDetails(refreshedOrder);
              localStorage.setItem('brella_order', JSON.stringify(refreshedOrder));
            }
          } catch (refreshError) {
            console.error('Error refreshing order details after claim cancellation:', refreshError);
          }
        }
        
        // Simple page refresh to ensure everything is properly updated
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      console.error('Error cancelling claim:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!mockStep) return;

    // console.log('ClaimsPage: mockStep detected:', mockStep);

    if (mockStep !== ClaimStates.ORDER_LOOKUP) {
      const mockOrder = {
        ...mockOrderData,
        claimId: 'claim-001',
      };

      setOrderDetails(mockOrder);
      setCustomer(mockOrder.customer);
      setTrackingInfo(mockOrder.trackingInfo);
      setShopifyProducts(mockOrder.shopifyProducts);
      
      // Set the step to the mock step if it's not ORDER_LOOKUP
      if (mockStep !== ClaimStates.ORDER_LOOKUP) {
        // console.log('ClaimsPage: Setting step to mock step:', mockStep);
        setStep(mockStep);
      }
    } else {
      setOrderDetails(null);
      setCustomer(null);
      setTrackingInfo(null);
      setShopifyProducts(null);
      setStep(ClaimStates.ORDER_LOOKUP);
    }
  }, [mockStep, setStep]);

  const isOrderLookup = !orderDetails || !customer;

  return (
    <div className="d-flex-column justify-content-center align-items-center returns-page w-100 py-4 px-3 px-sm-4"
    style={{ minHeight: '100vh', backgroundColor: isOrderLookup ? undefined : theme.backgroundColor }}
    >
      <div>
        {(!orderDetails || !customer) ? (
          <OrderLookup
            onOrderFound={setOrderDetails}
            onCustomerFound={setCustomer}
            onTrackingInfoFound={setTrackingInfo}
            onProductDetailsFound={setShopifyProducts}
            flowType="CLAIM"
          />
        ) : (
          // If we're in mock mode and not on ORDER_SUMMARY, skip the OrderSummary
          (mockStep && step !== ClaimStates.ORDER_SUMMARY) ? (
            <ClaimsWizard
              initialOrderDetails={orderDetails}
              trackingInfo={trackingInfo}
              customer={customer}
              shopifyProducts={shopifyProducts}
              step={step}
              setStep={setStep}
              updateOrderDetails={setOrderDetails}
            />
          ) : step === ClaimStates.ORDER_SUMMARY ? (
            <OrderSummary
              orderDetails={orderDetails}
              updateOrderDetails={setOrderDetails}
              customer={customer}
              trackingInfo={trackingInfo}
              onStepChange={setStep}
              shopifyProducts={shopifyProducts}
              mode="claims"
              buttonText={orderDetails.claimId && orderDetails.claimId !== '' ? "Claim Filed" : "Start Claim"}
              onButtonClick={() => setStep(ClaimStates.CHOOSE_CLAIM_REASON)}
              isButtonDisabled={orderDetails.claimId && orderDetails.claimId !== ''}
              buttonLoadingText="Claim submitted"
              claimDetails={claimDetails}
              onCancelClaim={handleCancelClaim}
              ReturnPolicyLink={ReturnPolicyLink}
              orderClaims={orderDetails.orderClaims}
            />
          ) : (
            <ClaimsWizard
              initialOrderDetails={orderDetails}
              trackingInfo={trackingInfo}
              customer={customer}
              shopifyProducts={shopifyProducts}
              step={step}
              setStep={setStep}
              updateOrderDetails={setOrderDetails}
            />
          )
        )}
      </div>
    </div>
  );
}
